import { createContext, useContext, useEffect, useMemo, useRef, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { useLocation } from "react-router-dom";
import { useAuthenticatedResource } from "../hooks/useAuthenticatedResource";

const PoolBrowsingContext = createContext();

function selectPools(data) {
    if (!Array.isArray(data?.pools) || data.pools.some(pool => typeof pool?.descriptor?.poolName !== "string")) {
        throw new Error("The pool service returned an unexpected response.");
    }
    return data.pools;
}

function PoolBrowsingSession({ children }) {
    const location = useLocation();
    const isList = location.pathname.replace(/\/$/, "") === "/pools";
    const [requested, setRequested] = useState(isList);
    const [listSearch, setListSearch] = useState("");
    const listScroll = useRef({ search: "", top: 0 });
    const pools = useAuthenticatedResource("/api/GetCstPoolEntries?includeElements=false", selectPools,
        { enabled: requested, retainData: true });

    useEffect(() => { if (isList) setRequested(true); }, [isList]);
    useEffect(() => { if (isList) setListSearch(location.search); }, [isList, location.search]);
    const value = useMemo(() => ({
        pools, listHref: `/pools${listSearch}`, listScroll,
    }), [pools, listSearch]);
    return <PoolBrowsingContext.Provider value={value}>{children}</PoolBrowsingContext.Provider>;
}

export function PoolBrowsingProvider({ children }) {
    const { accounts } = useMsal();
    return <PoolBrowsingSession key={accounts[0]?.homeAccountId || "signed-out"}>{children}</PoolBrowsingSession>;
}

export function usePoolBrowsing() {
    const context = useContext(PoolBrowsingContext);
    if (!context) throw new Error("usePoolBrowsing must be used within a PoolBrowsingProvider");
    return context;
}
